import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, ShieldAlert, X } from 'lucide-react';
import { consent, subjectsWizard, ApiError } from '@/api/client';

export function Step5Enroll({
  subjectId,
  activeConsentRecordId,
  onComplete,
  triggerRef,
  onPendingChange,
  onLabelChange,
  onNextDisabledChange,
}: {
  subjectId: string;
  activeConsentRecordId?: string | null;
  onComplete: () => void;
  triggerRef?: React.MutableRefObject<(() => void) | null>;
  onPendingChange?: (pending: boolean) => void;
  onLabelChange?: (label: string) => void;
  onNextDisabledChange?: (disabled: boolean) => void;
}) {
  const [enrolled, setEnrolled] = useState(false);
  const finalizeTriggeredRef = useRef(false);
  const qc = useQueryClient();
  // Sprint-18 — surfaces the expiration-gate's blocked-document list in a
  // dialog instead of a bare toast. There is no override (dropped 1 Oct
  // 2026): the expired document must be renewed first.
  const [expirationBlock, setExpirationBlock] = useState<string | null>(null);

  // Always load consent records so we can detect any pending consent record
  // even when the webhook hasn't fired (activeConsentRecordId still null in local dev).
  const recordsQ = useQuery({
    queryKey: ['consent', 'records', subjectId],
    queryFn: () => consent.listRecordsForSubject(subjectId),
    enabled: !!subjectId,
    refetchInterval: 10_000,
  });

  const records = recordsQ.data?.records ?? [];

  // Prefer the record the wizard explicitly marked active; fall back to the most
  // recent non-withdrawn, non-declined record so we can detect signing via poll
  // even when the webhook hasn't updated active_consent_record_id in the DB.
  const activeRecord = activeConsentRecordId
    ? records.find((r) => r.consent_record_id === activeConsentRecordId)
    : undefined;

  const pendingRecord = activeRecord
    ?? [...records]
        .filter((r) => !r.withdrawn_at && !r.declined_at)
        .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())[0];

  // Poll DocuSeal submission status to detect signing even when webhook hasn't fired.
  const submissionStatusQ = useQuery({
    queryKey: ['consent', 'submission-status', pendingRecord?.consent_record_id],
    queryFn: () => consent.submissionStatus(pendingRecord!.consent_record_id),
    enabled: !!pendingRecord?.consent_record_id && !pendingRecord?.signed_at,
    refetchInterval: 10_000,
    staleTime: 8_000,
  });
  const docusealStatus = submissionStatusQ.data;

  // signed_at set by webhook; docusealStatus covers local dev where webhook can't reach localhost.
  const consentFullySigned =
    !!pendingRecord?.signed_at ||
    (docusealStatus?.subject_signed === true && docusealStatus?.staff_signed === true);
  const subjectHasSigned = consentFullySigned || docusealStatus?.subject_signed === true;

  // When DocuSeal confirms both signed but the webhook hasn't updated the DB yet,
  // auto-call finalize to set signed_at + active_consent_record_id + subject.status='consented'.
  // This unblocks the enroll handler and updates the subjects list status badge.
  const finalize = useMutation({
    mutationFn: () => consent.finalizeConsent(pendingRecord!.consent_record_id),
    onSuccess: () => {
      // Refresh consent records + subject list so status badge updates to "Consented".
      qc.invalidateQueries({ queryKey: ['consent', 'records', subjectId] });
      qc.invalidateQueries({ queryKey: ['subjects'] });
    },
    onError: () => {
      // Non-fatal — enroll handler will still work because DocuSeal confirms both signed.
    },
  });

  useEffect(() => {
    if (!pendingRecord?.consent_record_id) return;
    if (pendingRecord.signed_at) return; // webhook already handled it
    if (!docusealStatus?.subject_signed || !docusealStatus.staff_signed) return;
    if (finalizeTriggeredRef.current || finalize.isPending || finalize.isSuccess) return;
    finalizeTriggeredRef.current = true;
    finalize.mutate();
  }, [docusealStatus, pendingRecord, finalize]);

  // No eligibility gate any more: the Eligibility step was removed (16 Oct 2026).
  const canEnroll = consentFullySigned;

  const enroll = useMutation({
    mutationFn: () => subjectsWizard.enroll(subjectId, {}),
    onSuccess: (r) => {
      setEnrolled(true);
      setExpirationBlock(null);
      toast.success(
        r.already_finalised
          ? 'Screening was already finalised for this subject.'
          : 'Screening finalised. The subject is now Screened.',
      );
    },
    onError: (e) => {
      // Sprint-18 — recognise the BE expiration-gate failure (HTTP 422
      // with the gate's structured message). Open the dialog instead of
      // a bare toast so the user sees the blocked documents. Other errors
      // fall through to the existing toast.
      const msg = e instanceof ApiError ? e.message : String(e);
      const isExpirationBlock =
        /expir/i.test(msg) && /(DoA|Delegation|1572|IRB|EC Approval|Lab Certif)/i.test(msg);
      if (isExpirationBlock) {
        setExpirationBlock(msg);
      } else {
        toast.error(`Enrolment blocked: ${msg}`);
      }
    },
  });

  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = enrolled ? onComplete : () => enroll.mutate();
    }
    onLabelChange?.(enrolled ? 'Finish' : 'Final');
    onNextDisabledChange?.(!canEnroll);
  });

  useEffect(() => {
    onPendingChange?.(enroll.isPending);
  }, [enroll.isPending, onPendingChange]);

  const consentLoading = recordsQ.isLoading || (!!pendingRecord && !docusealStatus && submissionStatusQ.isLoading);
  const prereqItems = [
    { label: 'Screening passed', ok: true },
    { label: 'Demographics recorded', ok: true },
    {
      label: consentFullySigned
        ? 'Consent signed (subject + co-signer)'
        : subjectHasSigned
          ? 'Consent: subject signed — awaiting co-signer'
          : consentLoading
            ? 'Consent: checking…'
            : 'Consent not yet signed',
      ok: consentFullySigned,
      loading: consentLoading,
    },
  ];

  if (enrolled) {
    return (
      <div className="space-y-5">
        <h2 className="text-base font-semibold text-ink-primary">Screened</h2>
        <div className="flex items-start gap-3 rounded border border-emerald-300 bg-emerald-50 px-4 py-4">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">
              Screening is complete. The subject is now <strong>Screened</strong>.
            </p>
            <p className="mt-1 text-xs text-emerald-800">
              Enrolment is a separate step: set the status to Enrolled on the
              subject profile when they are ready to start. The enrolment
              target is checked at that point, not here.
            </p>
            <p className="mt-1 text-xs text-emerald-700">
              Click <strong>Finish</strong> to close the wizard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold text-ink-primary">Final</h2>
      <p className="text-xs text-ink-muted">
        {canEnroll
          ? <>All prerequisite steps are complete. Review the checklist below, then click{' '}<strong>Final</strong> to finalise screening. The subject becomes <strong>Screened</strong>; enrolment is a separate step on their profile.</>
          : 'Enrolment is blocked. Correct the items marked below before proceeding.'}
      </p>

      {!consentFullySigned && pendingRecord && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {subjectHasSigned
            ? <>The subject has signed. Waiting for the staff co-signer (PI) to countersign via the signature queue.</>
            : <>Consent has not been fully signed. The subject and co-signer must both sign before enrolment.</>}
        </div>
      )}


      <div className="rounded-lg border border-surface-border bg-white p-4">
        <p className="mb-3 text-xs font-semibold text-ink-secondary uppercase tracking-wider">
          Pre-enrolment checklist
        </p>
        <ul className="space-y-2.5">
          {prereqItems.map((item) => (
            <li key={item.label} className={`flex items-center gap-2.5 text-sm ${item.ok ? 'text-ink-secondary' : 'text-amber-800'}`}>
              {item.loading
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ink-muted" />
                : item.ok
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  : <XCircle className="h-4 w-4 shrink-0 text-amber-500" />}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {expirationBlock && (
        <ExpirationBlockDialog message={expirationBlock} onClose={() => setExpirationBlock(null)} />
      )}
    </div>
  );
}

/**
 * Sprint-18 (eISF spec §edge case — Expired Delegation Log) — enrolment
 * blocked dialog. Server returns a structured error listing each expired
 * hard-block document (DoA, 1572, IRB letter, etc.) when a coordinator
 * tries to enrol a subject. The dialog surfaces the list verbatim + the
 * remediation path: renew the expired document FIRST (file a new version +
 * transition it to Effective). There is no override (dropped 1 Oct 2026).
 */
function ExpirationBlockDialog({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-ink-primary">Enrolment blocked — regulatory documents expired</div>
            <div className="mt-1 text-xs text-ink-muted">
              The site has hard-block regulatory documents past their expiry date.
              The proper remediation is to file a renewed version and transition
              it to Effective before enrolling.
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 whitespace-pre-wrap break-words">
          {message}
        </div>

        <div className="rounded border border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
          Renew the expired document(s) and transition them to Effective, then enrol.
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-ghost">Close</button>
        </div>
      </div>
    </div>
  );
}
